import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { AxiosResponse } from "axios";

import { getCompanyContactBook, getScheduleOption, getCountries } from "api";
import PickupDeliveryContact from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryContact";
import { IScheduleOptions, IDropdown, ITimezones } from "pages/shipper/RequestForm/components/Schedule/Types";
import errorToast from "helpers/errorToast";
import PickupDeliveryPlanning from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryPlanning";
import FormHeader from "components/v2/FormSection/components/FormHeader/FormHeader";
import FormSection from "components/v2/FormSection/FormSection";
import ScheduleAddress from "./ScheduleAddress";
import FormHr from "../../../../../RequestForm/components/Common/FormHr";
import { getOrderDateAndTimeRange, useFilteredOrders } from "./getOrderDateTimeRange";
import "./ScheduleForm.scss";

function ScheduleForm({ data }: any) {
  const intl = useIntl();
  const methods = useFormContext();
  const { setValue, getValues, watch } = methods;
  const location = watch("organizational.location");

  const { pickupDateRange, stop1DateRange } = getOrderDateAndTimeRange(data);
  const { filteredPickupOrders, filteredDeliveryOrders } = useFilteredOrders(data) || [];
  const isOffPlatformShipment = false;
  const formState: any = {
    disabledFields: [],
  };
  const [contactBook, setContactBook] = useState([]);
  const [scheduleOptions, setScheduleOptions] = useState<IScheduleOptions[]>([]);
  const [timezones] = useState<ITimezones[]>([]);
  const [countries, setCountries] = useState<IDropdown[]>([]);

  useEffect(() => {
    if (!location) return;
    getContactBook();
    getScheduleOptions();
    getAllCountries();
  }, [location]);

  const getAllCountries = () => {
    getCountries()
      .then((res) => {
        const filteredCountries = res.data.filter((country: { label: string }) => country.label !== "USA");
        setCountries(filteredCountries);
        const setCountryValues = (type: string) => {
          if (getValues(`schedule.${type}_country_code`)) {
            const country = res.data.filter((c: any) => c.value === getValues(`schedule.${type}_country_code`));
            if (country[0]?.label) setValue(`schedule.${type}_country`, country[0]?.label);
          }
        };
        setCountryValues("pickup");
        setCountryValues("stop1");
      })
      .catch((error) => errorToast(error));
  };

  const getScheduleOptions = () => {
    getScheduleOption(location)
      .then((res: AxiosResponse) => {
        setScheduleOptions(res.data.schedule_options);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveScheduleSetting",
            defaultMessage: "Could not retrieve schedule settings. Please try again later.",
          }),
        ),
      );
  };

  const getContactBook = () => {
    if (!location) return;
    getCompanyContactBook({
      params: {
        location,
      },
    })
      .then((res: AxiosResponse) => setContactBook(res.data.results))
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "new-request.schedule.couldNotRetrieveContactBook",
            defaultMessage: "Could not retrieve contact book. Please try again later.",
          }),
        ),
      );
  };

  return (
    <div className="schedule-container radius-2xl mt-3xl">
      <FormSection>
        <FormHeader title="Schedule" />
        <div className="form-section-content">
          <ScheduleAddress isPickupOrDelivery="pickup" />
          <PickupDeliveryContact
            isOffPlatformShipment={isOffPlatformShipment}
            isPickupOrDelivery="pickup"
            scheduleOptions={scheduleOptions}
            contactBook={contactBook}
            formState={formState}
            isConsolidated
          />
          <PickupDeliveryPlanning
            isOffPlatformShipment={isOffPlatformShipment}
            isConsolidated
            isPickupOrDelivery="pickup"
            scheduleOptions={scheduleOptions}
            isMiniTender={false}
            formState={formState}
            countries={countries}
            timezones={timezones}
            pickupDateRange={pickupDateRange}
            filteredPickupOrders={filteredPickupOrders}
          />
          <FormHr margin="3xl" />
          <ScheduleAddress isPickupOrDelivery="stop1" />
          <PickupDeliveryContact
            isOffPlatformShipment={isOffPlatformShipment}
            isPickupOrDelivery="stop1"
            scheduleOptions={scheduleOptions}
            contactBook={contactBook}
            formState={formState}
            isConsolidated
          />
          <PickupDeliveryPlanning
            isPickupOrDelivery="stop1"
            isOffPlatformShipment={isOffPlatformShipment}
            isConsolidated
            scheduleOptions={scheduleOptions}
            isMiniTender={false}
            formState={formState}
            countries={countries}
            timezones={timezones}
            stop1DateRange={stop1DateRange}
            filteredDeliveryOrders={filteredDeliveryOrders}
          />
        </div>
      </FormSection>
    </div>
  );
}

export default ScheduleForm;
